/* Admin editor for broker/particulier listings — supports editing ALL fields
   stored in sale_listings/makelaar_listings, managing photos, previewing the
   public shape, and saving-with-approval. All actions hit admin-scoped
   endpoints that require require_admin() server-side. */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Loader2, Save, CheckCircle2, XCircle, Eye, Trash2, ArrowLeft, Upload,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, compressImageFile, shrinkGallery } from "../lib/api";
import { SortableGallery } from "../components/gallery/SortableGallery";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AdminDocumentCheck } from "../components/admin/AdminDocumentCheck";
import { ListingTrafficPanel } from "../components/admin/ListingTrafficPanel";

const MOD_BADGE = {
  pending: { label: "Wacht op review", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Live", cls: "bg-success/15 text-success" },
  rejected: { label: "Afgewezen", cls: "bg-danger/10 text-danger" },
};

const TYPES = ["huis", "appartement", "studio", "penthouse", "villa", "bungalow", "overig"];
const ENERGY = ["A", "B", "C", "D", "E", "F", "G", "—"];
const KOOP_STATUSES = ["concept", "actief", "onder_bod", "verkocht_ov", "verkocht", "ingetrokken"];

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-semibold text-navy">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const AdminListingEdit = () => {
  const { kind, lid } = useParams();
  const navigate = useNavigate();
  useSeo({ title: "Listing bewerken · Admin" });
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const dirty = useRef(false);

  const load = () => {
    setData(null);
    api(`/admin/broker-listings/${kind}/${lid}`)
      .then((d) => { setData(d); setOriginal(d); dirty.current = false; })
      .catch((e) => { toast.error(e.message); navigate("/admin/broker-listings"); });
  };
  useEffect(load, [kind, lid]); // eslint-disable-line

  const set = (k, v) => { dirty.current = true; setData((p) => ({ ...p, [k]: v })); };

  const uploadPhotos = async (files) => {
    const current = data.gallery || [];
    const room = Math.max(0, 40 - current.length);
    if (room === 0) { toast.error("Maximaal 40 foto's per advertentie."); return; }
    const arr = Array.from(files).slice(0, room);
    try {
      const encoded = await Promise.all(arr.map((file) => compressImageFile(file)));
      set("gallery", [...current, ...encoded]);
    } catch { toast.error("Uploaden mislukt"); }
  };
  const removePhoto = (i) => set("gallery", (data.gallery || []).filter((_, idx) => idx !== i));

  const toPayload = () => {
    if (!data) return {};
    const base = {
      title: data.title, street: data.street, house_number: data.house_number,
      house_number_addition: data.house_number_addition, postcode: data.postcode,
      city: data.city, province: data.province, neighborhood: data.neighborhood,
      price: numOrNull(data.price), description: data.description,
      image: data.image, gallery: data.gallery, floorplans: data.floorplans,
      status: data.status, available_from: data.available_from,
      agency_name: data.agency_name, slug: data.slug,
    };
    if (kind === "koop") {
      Object.assign(base, {
        property_type: data.property_type, price_condition: data.price_condition,
        lat: numOrNull(data.lat), lng: numOrNull(data.lng),
        m2: numOrNull(data.m2), plot_m2: numOrNull(data.plot_m2),
        volume_m3: numOrNull(data.volume_m3), rooms: numOrNull(data.rooms),
        bedrooms: numOrNull(data.bedrooms), bathrooms: numOrNull(data.bathrooms),
        floors: numOrNull(data.floors), year_built: numOrNull(data.year_built),
        energy_label: data.energy_label, heating: data.heating, hot_water: data.hot_water,
        insulation: data.insulation, balcony: !!data.balcony, garden: data.garden,
        garage: !!data.garage, parking: !!data.parking, storage: !!data.storage,
        elevator: !!data.elevator, accessibility: data.accessibility, ownership: data.ownership,
        leasehold: data.leasehold, vve: data.vve, vve_contribution: numOrNull(data.vve_contribution),
        service_costs: numOrNull(data.service_costs), monument: data.monument,
        special_notes: data.special_notes, features: data.features || [],
        video_url: data.video_url, delivery: data.delivery, viewings_allowed: !!data.viewings_allowed,
        seller_name: data.seller_name, seller_email: data.seller_email,
        seller_phone: data.seller_phone, contact_preference: data.contact_preference,
      });
    } else {
      Object.assign(base, {
        m2: numOrNull(data.m2), bedrooms: numOrNull(data.bedrooms), energy_label: data.energy_label,
        furnished: !!data.furnished, pets: !!data.pets, balcony: !!data.balcony, parking: !!data.parking,
        income_required: numOrNull(data.income_required), lat: numOrNull(data.lat), lng: numOrNull(data.lng),
        published: data.published !== false,
      });
    }
    return base;
  };

  const save = async (andApprove = false) => {
    setSaving(true);
    try {
      // Shrink oversized gallery/floorplan entries so the Mongo doc fits
      // under the 16 MB BSON limit even when the admin inherits a legacy
      // listing full of 1-2 MB photos.
      const [safeGallery, safeFloorplans] = await Promise.all([
        shrinkGallery(data.gallery || []),
        shrinkGallery(data.floorplans || []),
      ]);
      const payload = { ...toPayload(), gallery: safeGallery, floorplans: safeFloorplans };
      if (payload.image && typeof payload.image === "string" && payload.image.startsWith("data:image/")) {
        payload.image = (await shrinkGallery([payload.image]))[0];
      }
      const qs = andApprove ? "?and_approve=true" : "";
      const res = await api(`/admin/broker-listings/${kind}/${lid}${qs}`, {
        method: "PATCH", body: JSON.stringify(payload),
      });
      toast.success(andApprove ? "Opgeslagen en goedgekeurd" : "Opgeslagen");
      setData(res); setOriginal(res); dirty.current = false;
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const moderate = async (action) => {
    const note = action === "reject" ? (window.prompt("Reden van afwijzing (optioneel):", "") ?? "") : "";
    setBusy(action);
    try {
      await api(`/admin/broker-listings/${kind}/${lid}/moderate`, {
        method: "POST", body: JSON.stringify({ action, note }),
      });
      toast.success(action === "approve" ? "Goedgekeurd" : "Afgewezen");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm("Deze listing definitief verwijderen? Gerelateerde favorites/views worden ook opgeruimd.")) return;
    setBusy("delete");
    try {
      await api(`/admin/broker-listings/${kind}/${lid}`, { method: "DELETE" });
      toast.success("Verwijderd");
      navigate("/admin/broker-listings");
    } catch (e) { toast.error(e.message); setBusy(null); }
  };

  if (!data) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" /></div>;
  const mod = MOD_BADGE[data.moderation_status || "approved"];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <Link to="/admin/broker-listings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy">
        <ArrowLeft size={14} /> Alle listings
      </Link>

      {/* header */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-navy" data-testid="admin-edit-title">
            {kind === "koop" ? "Koopwoning" : "Huurwoning"} bewerken
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${mod.cls}`}>
              {data.moderation_status === "pending" && <Clock size={11} />} {mod.label}
            </span>
            {data.seller_type === "particulier" && (
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900">Particulier</span>
            )}
            {data.broker?.email && (
              <span className="text-xs text-slate-500">· {data.broker.name || data.broker.email}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-preview-btn">
            <Link to={`/admin/preview/${kind}/${lid}`} target="_blank" rel="noreferrer">
              <Eye size={14} className="mr-1.5" /> Voorbeeld bekijken
            </Link>
          </Button>
          <Button onClick={() => save(false)} disabled={saving} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="save-btn">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            Wijzigingen opslaan
          </Button>
          {data.moderation_status !== "approved" && (
            <Button onClick={() => save(true)} disabled={saving} className="rounded-xl bg-success hover:bg-success/90" data-testid="save-approve-btn">
              <CheckCircle2 size={14} className="mr-1.5" /> Opslaan & goedkeuren
            </Button>
          )}
          {data.moderation_status !== "rejected" && (
            <Button variant="outline" onClick={() => moderate("reject")} disabled={busy === "reject"} className="rounded-xl text-amber-800" data-testid="reject-btn">
              <XCircle size={14} className="mr-1.5" /> Afwijzen
            </Button>
          )}
          <Button variant="outline" onClick={remove} disabled={busy === "delete"} className="rounded-xl text-danger hover:bg-danger/5" data-testid="delete-btn">
            {busy === "delete" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
            Verwijderen
          </Button>
        </div>
      </div>

      {/* audit */}
      <div className="mt-6 rounded-2xl border border-border bg-slate-50 p-4 text-xs text-slate-600">
        <div className="grid gap-2 sm:grid-cols-3">
          <div><span className="font-semibold text-slate-500">ID:</span> {data.id}</div>
          <div><span className="font-semibold text-slate-500">Slug:</span> {data.slug || "—"}</div>
          <div><span className="font-semibold text-slate-500">Seller type:</span> {data.seller_type || "makelaar"}</div>
          <div><span className="font-semibold text-slate-500">Aangemaakt:</span> {data.created_at || "—"}</div>
          <div><span className="font-semibold text-slate-500">Ingediend:</span> {data.submitted_at || "—"}</div>
          <div><span className="font-semibold text-slate-500">Laatst bijgewerkt:</span> {data.updated_at || "—"}</div>
          {data.moderation_decided_at && <div><span className="font-semibold text-slate-500">Beslist op:</span> {data.moderation_decided_at}</div>}
          {data.moderation_decided_by && <div><span className="font-semibold text-slate-500">Beslist door:</span> {data.moderation_decided_by}</div>}
          {data.moderation_note && <div className="sm:col-span-3"><span className="font-semibold text-slate-500">Notitie:</span> {data.moderation_note}</div>}
          {data.seller_type === "particulier" && (
            <>
              <div><span className="font-semibold text-slate-500">Terms geaccepteerd:</span> {data.terms_version_accepted || "—"}</div>
              <div><span className="font-semibold text-slate-500">Verklaring v{data.seller_declaration_version || "—"}:</span> {data.seller_declaration_accepted_at || "—"}</div>
            </>
          )}
        </div>
      </div>

      {/* Documentcheck + audit log — alleen voor sale listings (koop) */}
      {kind === "koop" && (
        <>
          <div className="mt-6">
            <ListingTrafficPanel lid={lid} defaultOpen={true} />
          </div>
          <AdminDocumentCheck lid={lid} listing={data} onSaved={(d) => setData((p) => ({ ...p, ...d }))} />
        </>
      )}

      {/* Form */}
      <div className="mt-6 space-y-6">
        {kind === "koop" && data.views && (
          <section className="rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid="listing-views">
            <h2 className="font-display text-lg font-bold text-navy">Statistieken</h2>
            <p className="mt-1 text-sm text-slate-500">Aantal keer dat deze woning is bekeken op WoningAlert.</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-4">
              <div className="rounded-2xl bg-brandblue-light p-4">
                <div className="text-xs font-semibold uppercase text-brandblue">Totaal</div>
                <div className="mt-1 font-display text-3xl font-extrabold text-navy">{data.views.total}</div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">7 dagen</div>
                <div className="mt-1 font-display text-3xl font-extrabold text-navy">{data.views.last_7d}</div>
                <div className="text-xs text-slate-400">{data.views.unique_7d} unieke bezoekers</div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">30 dagen</div>
                <div className="mt-1 font-display text-3xl font-extrabold text-navy">{data.views.last_30d}</div>
                <div className="text-xs text-slate-400">{data.views.unique_30d} unieke bezoekers</div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">Gemiddeld/dag (30d)</div>
                <div className="mt-1 font-display text-3xl font-extrabold text-navy">{Math.round((data.views.last_30d || 0) / 30)}</div>
              </div>
            </div>
          </section>
        )}

        {/* Basis */}
        <section className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-navy">Basis</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Titel"><Input value={data.title || ""} onChange={(e) => set("title", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Slug"><Input value={data.slug || ""} onChange={(e) => set("slug", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Straat"><Input value={data.street || ""} onChange={(e) => set("street", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Huisnummer"><Input value={data.house_number || ""} onChange={(e) => set("house_number", e.target.value)} className="rounded-xl" /></Field>
                <Field label="Toevoeging"><Input value={data.house_number_addition || ""} onChange={(e) => set("house_number_addition", e.target.value)} className="rounded-xl" /></Field>
              </div>
            )}
            <Field label="Plaats"><Input value={data.city || ""} onChange={(e) => set("city", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Postcode"><Input value={data.postcode || ""} onChange={(e) => set("postcode", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && <Field label="Provincie"><Input value={data.province || ""} onChange={(e) => set("province", e.target.value)} className="rounded-xl" /></Field>}
            <Field label="Buurt"><Input value={data.neighborhood || ""} onChange={(e) => set("neighborhood", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Prijs (€)"><Input type="number" value={data.price ?? ""} onChange={(e) => set("price", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && (
              <>
                <Field label="Prijsconditie">
                  <Select value={data.price_condition || "kk"} onValueChange={(v) => set("price_condition", v)}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="kk">k.k.</SelectItem><SelectItem value="von">v.o.n.</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Woningtype">
                  <Select value={data.property_type || "huis"} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={data.status || "actief"} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{KOOP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </>
            )}
          </div>
        </section>

        {/* Kenmerken */}
        <section className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-navy">Kenmerken</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="Woonopp. (m²)"><Input type="number" value={data.m2 ?? ""} onChange={(e) => set("m2", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && <Field label="Perceel (m²)"><Input type="number" value={data.plot_m2 ?? ""} onChange={(e) => set("plot_m2", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="Inhoud (m³)"><Input type="number" value={data.volume_m3 ?? ""} onChange={(e) => set("volume_m3", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="Kamers"><Input type="number" value={data.rooms ?? ""} onChange={(e) => set("rooms", e.target.value)} className="rounded-xl" /></Field>}
            <Field label="Slaapkamers"><Input type="number" value={data.bedrooms ?? ""} onChange={(e) => set("bedrooms", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && <Field label="Badkamers"><Input type="number" value={data.bathrooms ?? ""} onChange={(e) => set("bathrooms", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="Woonlagen"><Input type="number" value={data.floors ?? ""} onChange={(e) => set("floors", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="Bouwjaar"><Input type="number" value={data.year_built ?? ""} onChange={(e) => set("year_built", e.target.value)} className="rounded-xl" /></Field>}
            <Field label="Energielabel">
              <Select value={data.energy_label || "—"} onValueChange={(v) => set("energy_label", v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{ENERGY.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Beschikbaar vanaf"><Input value={data.available_from || ""} onChange={(e) => set("available_from", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && <Field label="Tuin"><Input value={data.garden || ""} onChange={(e) => set("garden", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="VvE p/m (€)"><Input type="number" value={data.vve_contribution ?? ""} onChange={(e) => set("vve_contribution", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="Servicekosten (€)"><Input type="number" value={data.service_costs ?? ""} onChange={(e) => set("service_costs", e.target.value)} className="rounded-xl" /></Field>}
            {kind === "koop" && <Field label="Eigendom"><Input value={data.ownership || ""} onChange={(e) => set("ownership", e.target.value)} className="rounded-xl" /></Field>}
            <div className="sm:col-span-3 grid gap-3 sm:grid-cols-4">
              {(kind === "koop" ? [["garage", "Garage"], ["parking", "Parkeerplek"], ["balcony", "Balkon"], ["elevator", "Lift"], ["storage", "Berging"], ["monument", "Monument"], ["viewings_allowed", "Bezichtiging toegestaan"]]
                : [["furnished", "Gemeubileerd"], ["pets", "Huisdieren OK"], ["balcony", "Balkon"], ["parking", "Parkeerplek"], ["published", "Gepubliceerd"]]).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 rounded-xl border border-border bg-white p-3 text-sm font-medium text-navy">
                  <Switch checked={!!data[k]} onCheckedChange={(v) => set(k, v)} /> {label}
                </label>
              ))}
            </div>
            <div className="sm:col-span-3">
              <Field label="Omschrijving"><Textarea rows={6} value={data.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-xl" /></Field>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-navy">Contactinformatie</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Agency / Verkoper"><Input value={data.agency_name || ""} onChange={(e) => set("agency_name", e.target.value)} className="rounded-xl" /></Field>
            {kind === "koop" && (
              <>
                <Field label="Verkoper naam"><Input value={data.seller_name || ""} onChange={(e) => set("seller_name", e.target.value)} className="rounded-xl" /></Field>
                <Field label="E-mail"><Input type="email" value={data.seller_email || ""} onChange={(e) => set("seller_email", e.target.value)} className="rounded-xl" /></Field>
                <Field label="Telefoon"><Input value={data.seller_phone || ""} onChange={(e) => set("seller_phone", e.target.value)} className="rounded-xl" /></Field>
                <Field label="Contactvoorkeur">
                  <Select value={data.contact_preference || "email"} onValueChange={(v) => set("contact_preference", v)}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="phone">Telefoon</SelectItem>
                      <SelectItem value="both">Beide</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}
          </div>
        </section>

        {/* Photos */}
        <section className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-navy">Foto&apos;s ({data.gallery?.length || 0} / 40)</h2>
          <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-white py-6 text-sm font-semibold text-brandblue hover:border-brandblue">
            <Upload size={16} /> Foto&apos;s uploaden
            <input type="file" accept="image/*" multiple onChange={(e) => uploadPhotos(e.target.files)} className="hidden" />
          </label>
          <div className="mt-4">
            <SortableGallery
              value={data.gallery || []}
              onChange={(next) => { set("gallery", next); set("image", next[0] || null); }}
              onRemove={removePhoto}
              data-testid="admin-gallery"
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminListingEdit;
